import { SCROLL_CLASS_NAMES } from "../../models/scroll/ScrollHTMLClass";
import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

/**
 * Smooth scroll controller with delta-based acceleration and direction tracking.
 * Handles wheel events and scroll position updates with smooth interpolation.
 */
export class StringScrollSmooth extends ScrollController {
  /**
   * Unique identifier for this scroll controller type.
   * Used for selection and debug purposes.
   */
  public readonly name: string = "smooth";

  /**
   * Whether the user has manually scrolled using the native scrollbar.
   * When true, the scroll position is directly synced from DOM on next frame.
   */
  private isScrollbarManipulation = false;

  /**
   * Current internal force applied to the scroll target.
   * Calculated from accumulated scroll impulse and acceleration.
   */
  private scrollForce: number = 0;

  /**
   * Latest raw scroll impulse received from the wheel event (`deltaY`).
   * Temporarily stored for direction and boundary checks.
   */
  private wheelImpulse: number = 0;

  /**
   * Scroll position from the previous frame.
   * Used to detect actual movement and trigger updates only when needed.
   */
  private previousCurrent: number = 0;

  /**
   * Minimum velocity threshold below which scrolling is considered stopped.
   * Prevents micro-movements from continuing infinite smooth scroll.
   */
  private readonly velocityThreshold = 0.1;

  constructor(context: StringContext) {
    super(context);
  }

  /**
   * Handles scroll direction changes and toggles CSS classes accordingly.
   * @param newDirection `true` if scrolling down, `false` if up.
   */
  private updateScrollDirection(newDirection: boolean) {
    this.isLastBottomScrollDirection = newDirection;
    if (this.isBottomScrollDirection === null) {
      this.isBottomScrollDirection = newDirection;
      return;
    }
    this.context.data.scroll.isScrollingDown = newDirection;
    this.onChangeDirection();

    document.documentElement.classList.toggle(
      SCROLL_CLASS_NAMES.SCROLLING_FORWARD,
      newDirection
    );
    document.documentElement.classList.toggle(
      SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
      !newDirection
    );
    document.documentElement.classList.toggle(
      SCROLL_CLASS_NAMES.SCROLL_FORWARD,
      newDirection
    );
    document.documentElement.classList.toggle(
      SCROLL_CLASS_NAMES.SCROLL_BACKWARD,
      !newDirection
    );
  }

  /**
   * Immediately stops scrolling and resets all deltas and directions.
   */
  public stopScroll(): void {
    this.context.data.scroll.lerped = 0;
    this.context.data.scroll.delta = 0;
    this.context.data.scroll.target = this.context.data.scroll.current;
    this.isProg = false;
    this.onCalcUpdate();
    document.documentElement.classList.remove(
      SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
      SCROLL_CLASS_NAMES.SCROLLING_FORWARD
    );
    this.isBottomScrollDirection = null;
  }

  /**
   * Called on each animation frame to apply lerped scroll logic.
   */
  public onFrame(): void {
    if (this.isScrollbarManipulation) {
      this.isScrollbarManipulation = false;
      this.context.data.scroll.current =
        this.context.data.scroll.elementContainer.scrollTop;
      this.context.data.scroll.target =
        this.context.data.scroll.elementContainer.scrollTop;
      this.context.data.scroll.transformedCurrent =
        this.context.data.scroll.current *
        this.context.data.viewport.transformScale;
      return;
    }

    if (this.context.data.scroll.delta !== 0) {
      this.scrollForce =
        this.context.data.scroll.delta *
        this.context.data.scroll.speedAccelerate;

      this.context.data.scroll.target = Math.min(
        Math.max(0, this.context.data.scroll.target + this.scrollForce),
        this.context.data.scroll.bottomPosition
      );
      this.context.data.scroll.delta -= this.scrollForce;

      this.context.data.scroll.lerped =
        (this.context.data.scroll.target - this.context.data.scroll.current) *
        this.context.data.scroll.speed;

      const absVelocity = Math.abs(this.context.data.scroll.lerped);
      if (this.context.data.scroll.lerped > 0) {
        this.context.data.scroll.current = Math.ceil(
          this.context.data.scroll.current + this.context.data.scroll.lerped
        );
      } else {
        this.context.data.scroll.current = Math.floor(
          this.context.data.scroll.current + this.context.data.scroll.lerped
        );
      }

      this.context.data.scroll.transformedCurrent =
        this.context.data.scroll.current *
        this.context.data.viewport.transformScale;
      this.updateScrollDirection(this.context.data.scroll.lerped > 0);

      if (absVelocity < this.velocityThreshold) {
        this.stopScroll();
        this.onScrollStop();
      } else {
        this.isProg = true;
        if (this.previousCurrent !== this.context.data.scroll.current) {
          this.previousCurrent = this.context.data.scroll.current;
          this.onCalcUpdate();
        }
      }
    }
  }

  /**
   * Handles user wheel input to accumulate scroll delta.
   * @param e The WheelEvent from the browser.
   */
  public onWheel(e: WheelEvent): void {
    if (e.deltaY !== 0) {
      e.preventDefault();
    }

    this.wheelImpulse = e.deltaY;
    if (this.wheelImpulse === 0) return;

    if (this.context.data.scroll.delta === 0) {
      this.onScrollStart();
    }

    const scrollDirection = Math.sign(this.wheelImpulse);
    const atTop = this.context.data.scroll.target === 0 && scrollDirection < 0;
    const atBottom =
      this.context.data.scroll.target ===
        this.context.data.scroll.bottomPosition && scrollDirection > 0;
    if (atTop || atBottom) return;
    this.context.data.scroll.delta += this.wheelImpulse;
  }

  /**
   * Detects native scrollbar manipulation when smooth scroll is idle.
   * @param e Scroll event.
   */
  public onScroll(e: Event): void {
    if (!this.isProg) {
      this.isScrollbarManipulation = true;
    }
  }
}
